import React from "react";
import { Button as MuiButton, CircularProgress } from "@mui/material";

interface ButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  size?: "small" | "medium";
  variant?: "contained" | "outlined" | "text";
  color?:
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning"
    | "inherit";
  className?: string;
  ariaLabel?: string;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  disabled?: boolean;
  type?: "button" | "submit" | "reset";
  loading?: boolean;
}

const buttonTheme = (color: string, variant: string, disabled: boolean) => {
  if (disabled) {
    return "bg-hp-disabled-button text-hp-disabled-button-text cursor-not-allowed opacity-100";
  }

  switch (variant) {
    case "contained":
      switch (color) {
        case "primary":
          return "bg-hp-primary text-white [&>span]:text-white shadow-sm";
        // Add other contained color variants here
        default:
          return "";
      }
    case "outlined":
      switch (color) {
        case "primary":
          return "border-hp-primary text-hp-primary";
        // Add other outlined color variants here
        default:
          return "";
      }
    case "text":
      switch (color) {
        case "primary":
          return "text-hp-primary";
        // Add other text color variants here
        default:
          return "";
      }
    default:
      return "";
  }
};

const HPButton: React.FC<ButtonProps> = ({
  children,
  onClick,
  size = "medium",
  variant = "contained",
  color = "primary",
  className,
  ariaLabel,
  startIcon,
  endIcon,
  disabled,
  type,
  loading = false,
}) => {
  const sizeClasses =
    size === "small" ? "px-2 py-1 h-fit text-xs" : "px-6 h-10 text-sm";

  const spinnerSize = size === "small" ? 16 : 20;

  return (
    <MuiButton
      onClick={onClick}
      size={size}
      variant={variant}
      color={color}
      className={`capitalize [&>.MuiButton-startIcon]:mr-1 [&>.MuiButton-endIcon]:ml-1 ${sizeClasses} ${buttonTheme(
        color,
        variant,
        disabled || loading,
      )} ${className}`}
      aria-label={ariaLabel}
      startIcon={
        loading ? (
          <CircularProgress size={spinnerSize} className="text-white" />
        ) : (
          startIcon
        )
      }
      endIcon={endIcon}
      disabled={disabled || loading}
      type={type}
    >
      {children}
    </MuiButton>
  );
};

export default HPButton;
